/**
 * react-query keys + fetchers ต่อ endpoint — ใช้ type จาก @uwu/contracts เท่านั้น
 * (ฟิลด์ไม่กี่ตัวที่ contracts ยังไม่มี อยู่ใน contracts-w2.ts — ดูคอมเมนต์ในไฟล์นั้น)
 */
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { z } from "zod";
import type { GuestSession, Me, MyTicketCounts, NotificationList, NotificationPrefs, SavedView, ServiceRef, StatusSet, Tag, Ticket, TicketLinkList, TicketList, TicketListQuery, Watcher } from "@uwu/contracts";
import { api, qs } from "./api";
import { isHubRealtimeHealthy, isTicketRealtimeHealthy } from "./realtime";

/** [T-058] mentioned/snoozed มาจาก T-057 (worker ทำขนาน) — ส่งได้ทันที แต่ contracts บน main ยังไม่มี → widen ที่นี่ */
export type TicketView = NonNullable<TicketListQuery["view"]> | "mentioned" | "snoozed";
export type TicketSort = "last_activity_desc" | "created_desc" | "due_asc" | "priority_desc";

/** D-026: polling 15 วิ เฉพาะเมื่อแท็บ visible */
export function visibleInterval(): 15000 | false {
  return typeof document !== "undefined" && document.visibilityState === "visible" ? 15000 : false;
}

export const keys = {
  me: ["me"] as const,
  tickets: (view: TicketView, teamId?: string) => ["tickets", view, teamId ?? null] as const,
  ticket: (id: string) => ["ticket", id] as const,
  services: ["services"] as const,
  teams: ["teams"] as const,
  tags: ["tags"] as const,
  views: ["views"] as const,
  watchers: (ticketId: string) => ["watchers", ticketId] as const,
  guestMe: ["guest", "me"] as const,
  notifications: ["notifications"] as const,
  links: (ticketId: string) => ["links", ticketId] as const,
  redactions: (ticketId: string) => ["redactions", ticketId] as const,
};

export type TeamRef = { id: string; code: string; name: Record<string, string>; department_id: string | null; my_role: string | null };

export const teamsQuery = queryOptions({
  queryKey: keys.teams,
  queryFn: () => api.get<TeamRef[]>("/api/v1/teams"),
  staleTime: 5 * 60_000,
});

export const tagsQuery = queryOptions({
  queryKey: keys.tags,
  queryFn: async () => {
    const r = await api.get<Tag[] | { items: Tag[] }>("/api/v1/tags");
    return Array.isArray(r) ? r : r.items;
  },
  staleTime: 60_000,
  retry: false,
});

export const viewsQuery = queryOptions({
  queryKey: keys.views,
  queryFn: async () => {
    const r = await api.get<SavedView[] | { items: SavedView[] }>("/api/v1/views");
    return Array.isArray(r) ? r : r.items;
  },
  staleTime: 30_000,
  retry: false,
});

export const watchersQuery = (ticketId: string) =>
  queryOptions({
    queryKey: keys.watchers(ticketId),
    queryFn: async () => {
      const r = await api.get<Watcher[] | { items: Watcher[] }>(`/api/v1/tickets/${encodeURIComponent(ticketId)}/watchers`);
      return Array.isArray(r) ? r : r.items;
    },
    staleTime: 15_000,
    retry: false,
  });

export const guestMeQuery = queryOptions({
  queryKey: keys.guestMe,
  queryFn: () => api.get<GuestSession>("/api/v1/guest/me"),
  staleTime: 60_000,
  retry: false,
});


export const meQuery = queryOptions({
  queryKey: keys.me,
  queryFn: ({ signal }) => api.get<Me>("/api/v1/me", { signal }),
  staleTime: 60_000,
});

export const workflowStatusSetsQuery = queryOptions({
  queryKey: ["workflow", "status-sets"] as const,
  queryFn: async () => {
    const result = await api.get<StatusSet[] | { items: StatusSet[] }>("/api/v1/workflow/status-sets");
    return Array.isArray(result) ? result : result.items;
  },
  staleTime: 5 * 60_000,
  retry: false,
});

export type InboxQueryExtras = { q?: string; sort?: TicketSort; view_id?: string; status_type?: string | string[] };
/**
 * [UX-D1] view เป็น optional — เมื่อเปิดผ่าน saved view (view_id) และ URL ไม่ได้ระบุ view
 * ให้ filter.view ของ saved view เป็นเจ้าภาพฝั่ง server (applySavedViewFilter) แทนการยัด "mine" ทับ
 */
export const ticketListQuery = (view: TicketView | undefined, teamId?: string, extras: InboxQueryExtras = {}) =>
  infiniteQueryOptions({
    queryKey: ["tickets", view ?? null, teamId ?? null, extras.q ?? null, extras.sort ?? null, extras.view_id ?? null, extras.status_type ?? null] as const,
    queryFn: ({ pageParam, signal }) =>
      api.get<TicketList>(
        "/api/v1/tickets" + qs({ view, team_id: teamId, q: extras.q, sort: extras.sort, view_id: extras.view_id, status_type: extras.status_type, cursor: pageParam, limit: 50 }),
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 15_000,
    refetchInterval: visibleInterval,
  });

/** [UX-D1 · UX-43] counts ทั้งหมดของ /my จาก server (all/active/closed) — 404 (worker เก่า) → หน้าใช้ป้าย "โหลดแล้ว N" แทน */
export const myTicketCountsQuery = queryOptions({
  queryKey: ["my", "ticket-counts"] as const,
  queryFn: () => api.get<MyTicketCounts>("/api/v1/my/tickets/counts"),
  staleTime: 30_000,
  retry: false,
});

export const ticketQuery = (id: string) =>
  queryOptions({
    queryKey: keys.ticket(id),
    queryFn: ({ signal }) => api.get<Ticket>(`/api/v1/tickets/${encodeURIComponent(id)}`, { signal }),
    staleTime: 5_000,
    // D-026/R2: WS ทำงานอยู่ (useTicketSocket ต่อสำเร็จ) → ปิด polling, ให้ ticket_event invalidate แทน
    // ต่อไม่ได้ 3 ครั้งติด (offline) → isTicketRealtimeHealthy คืน false → กลับไป polling 15s เดิม
    refetchInterval: () => (isTicketRealtimeHealthy(id) ? false : visibleInterval()),
  });

/** R3: bell dropdown — 10 รายการล่าสุด; WS (`useHubSocket`) ทำงานอยู่ → ไม่ poll, ตัวเดียวกับ hub badge */
export const notificationBellQuery = queryOptions({
  queryKey: [...keys.notifications, "bell"] as const,
  queryFn: () => api.get<NotificationList>("/api/v1/notifications" + qs({ limit: 10 })),
  staleTime: 10_000,
  refetchInterval: () => (isHubRealtimeHealthy() ? false : visibleInterval()),
});

export const notificationListQuery = (unreadOnly: boolean) =>
  infiniteQueryOptions({
    queryKey: [...keys.notifications, "list", unreadOnly] as const,
    queryFn: ({ pageParam }) => api.get<NotificationList>("/api/v1/notifications" + qs({ unread: unreadOnly ? "1" : undefined, cursor: pageParam, limit: 50 })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 10_000,
  });

export const notificationPrefsQuery = queryOptions({
  queryKey: ["notification-prefs"] as const,
  queryFn: () => api.get<NotificationPrefs>("/api/v1/me/notification-prefs"),
  staleTime: 30_000,
});

export const ticketLinksQuery = (ticketId: string) =>
  queryOptions({
    queryKey: keys.links(ticketId),
    queryFn: () => api.get<TicketLinkList>(`/api/v1/tickets/${encodeURIComponent(ticketId)}/links`),
    staleTime: 10_000,
  });

/** contracts ยังไม่มี schema ของ list บริการ — รองรับทั้ง array และ {items} */
export type Service = z.infer<typeof ServiceRef>;
type ServiceList = Service[] | { items: Service[] };
export const servicesQuery = queryOptions({
  queryKey: keys.services,
  queryFn: async () => {
    const r = await api.get<ServiceList>("/api/v1/services");
    return Array.isArray(r) ? r : r.items;
  },
  staleTime: 5 * 60_000,
});
